import React, { useEffect } from 'react';
import { trackPageLifecycle } from '../lib/customerEventTracker';

// Mirrors the live content at https://solvetax.in/privacy verbatim.
const SECTIONS = [
  {
    h: '1. Definitions',
    blocks: [
      { type: 'list', items: [
        '"SolveTax", "Company", "We", "Our", or "Us" refers to SolveTax and its affiliates, employees, consultants, and authorized representatives.',
        '"Client", "User", or "You" refers to any individual, business entity, or organization availing our services.',
        '"Services" include taxation, GST compliance, income tax return filing, accounting, bookkeeping, ROC filings, company incorporation, registrations, advisory, and related financial services.',
      ] },
    ],
  },
  {
    h: '2. Acceptance of Terms',
    blocks: [
      { type: 'p', text: 'By using our services, you acknowledge that you have read, understood, and agreed to these Terms & Conditions. If you do not agree, you should discontinue use of our services immediately.' },
    ],
  },
  {
    h: '3. Scope of Services',
    blocks: [
      { type: 'p', text: 'SolveTax shall provide services based on information and documents submitted by the client. The scope of work shall be limited to the services specifically agreed upon in writing, proposal, invoice, engagement letter, or service package.' },
      { type: 'p', text: 'Any additional work requested beyond the agreed scope may be charged separately.' },
    ],
  },
  {
    h: '4. Client Responsibilities',
    blocks: [
      { type: 'p', text: 'The client agrees to:' },
      { type: 'list', items: [
        'Provide complete, accurate, and authentic information and documents.',
        'Submit information within prescribed timelines.',
        'Review and verify all returns, forms, applications, and documents before submission.',
        'Inform SolveTax promptly regarding any notices, communications, or changes affecting compliance obligations.',
      ] },
      { type: 'p', text: 'The client shall remain solely responsible for the accuracy and completeness of information provided.' },
    ],
  },
  {
    h: '5. Fees and Payment',
    blocks: [
      { type: 'list', items: [
        'Service fees shall be payable as per the agreed quotation, invoice, subscription plan, or engagement letter.',
        'Fees once paid are generally non-refundable unless otherwise agreed in writing.',
        'Delayed payments may result in suspension of services.',
        'Government fees, penalties, taxes, filing charges, certification charges, or third-party costs are separate from professional fees unless expressly included.',
      ] },
    ],
  },
  {
    h: '6. Timelines and Delays',
    blocks: [
      { type: 'p', text: 'SolveTax shall make reasonable efforts to complete assignments within expected timelines. However, timelines may vary due to:' },
      { type: 'list', items: [
        'Delayed client responses;',
        'Government portal downtime;',
        'Regulatory changes;',
        'Technical issues beyond our control;',
        'Delays by authorities or third parties.',
      ] },
      { type: 'p', text: 'SolveTax shall not be liable for delays caused by such circumstances.' },
    ],
  },
  {
    h: '7. Accuracy of Information',
    blocks: [
      { type: 'p', text: 'Our services are based entirely on information and documents provided by the client. SolveTax shall not be responsible for:' },
      { type: 'list', items: [
        'Incorrect disclosures;',
        'Omitted information;',
        'Misrepresentation by the client;',
        'Consequences arising from inaccurate data provided by the client.',
      ] },
    ],
  },
  {
    h: '8. Compliance and Regulatory Changes',
    blocks: [
      { type: 'p', text: 'Tax laws, rules, notifications, circulars, and regulations are subject to change. Advice provided by SolveTax is based on the laws and interpretations applicable on the date of service and may require revision if regulations change subsequently.' },
    ],
  },
  {
    h: '9. Limitation of Liability',
    blocks: [
      { type: 'p', text: 'To the maximum extent permitted by law:' },
      { type: 'list', items: [
        'SolveTax shall not be liable for indirect, incidental, consequential, special, or punitive damages.',
        'Our total liability shall not exceed the professional fees paid by the client for the specific service giving rise to the claim.',
        'We shall not be responsible for penalties, interest, losses, or legal consequences arising from inaccurate information supplied by the client.',
      ] },
    ],
  },
  {
    h: '10. Confidentiality',
    blocks: [
      { type: 'p', text: 'SolveTax shall maintain confidentiality of client information and documents except:' },
      { type: 'list', items: [
        'Where disclosure is required by law;',
        'To regulatory authorities;',
        'To professional advisors assisting in service delivery;',
        "With the client's consent.",
      ] },
    ],
  },
  {
    h: '11. Data Protection',
    blocks: [
      { type: 'p', text: 'The client authorizes SolveTax to collect, store, process, and use information necessary for providing services and complying with legal obligations.' },
      { type: 'p', text: 'Reasonable security measures shall be maintained to safeguard client data.' },
    ],
  },
  {
    h: '12. Intellectual Property',
    blocks: [
      { type: 'p', text: 'All content, software, designs, processes, templates, reports, presentations, and materials developed by SolveTax shall remain the intellectual property of SolveTax unless otherwise agreed in writing.' },
      { type: 'p', text: 'Clients may use deliverables solely for their internal business purposes.' },
    ],
  },
  {
    h: '13. Third-Party Platforms',
    blocks: [
      { type: 'p', text: 'SolveTax may utilize government portals, banking systems, software providers, cloud services, or third-party platforms. We are not responsible for interruptions, errors, security breaches, or service failures occurring on such platforms.' },
    ],
  },
  {
    h: '14. Termination',
    blocks: [
      { type: 'p', text: 'Either party may terminate the engagement by providing written notice.' },
      { type: 'list', items: [
        'Outstanding dues shall become immediately payable.',
        'SolveTax may discontinue ongoing work until all dues are cleared.',
        'Work completed up to the termination date shall remain chargeable.',
      ] },
      { type: 'p', text: 'Upon termination:' },
    ],
  },
  {
    h: '15. Indemnity',
    blocks: [
      { type: 'p', text: 'The client agrees to indemnify and hold harmless SolveTax against claims, losses, penalties, damages, or liabilities arising from:' },
      { type: 'list', items: [
        'False or misleading information provided by the client;',
        'Violation of applicable laws;',
        'Unauthorized use of services;',
        'Breach of these Terms & Conditions.',
      ] },
    ],
  },
  {
    h: '16. Force Majeure',
    blocks: [
      { type: 'p', text: 'SolveTax shall not be liable for failure or delay in performance caused by events beyond reasonable control, including natural disasters, pandemics, cyberattacks, government restrictions, power failures, strikes, or system outages.' },
    ],
  },
  {
    h: '17. Governing Law and Jurisdiction',
    blocks: [
      { type: 'p', text: 'These Terms & Conditions shall be governed by the laws of India. Any dispute arising out of these terms shall be subject to the exclusive jurisdiction of courts located in Hyderabad, Telangana.' },
    ],
  },
  {
    h: '18. Amendments',
    blocks: [
      { type: 'p', text: 'SolveTax reserves the right to modify these Terms & Conditions at any time. Updated terms shall become effective upon publication on the website or communication to clients.' },
    ],
  },
  {
    h: '19. Contact Information',
    blocks: [
      { type: 'p', text: 'SolveTax' },
      { type: 'p', text: 'Email: contact@solvetax.in' },
      { type: 'p', text: 'Phone: 9346004430 / 9346941147' },
      { type: 'p', text: 'Website: www.solvetax.in' },
    ],
  },
];

const SectionBlock = ({ block }) => {
  if (block.type === 'list') {
    return (
      <ul className="list-disc pl-5 space-y-1">
        {block.items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  return <p>{block.text}</p>;
};

const PrivacyPolicy = () => {
  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'PRIVACY_PAGE',
      form_name: 'PrivacyPolicy',
      service_code: null,
      open_event_name: 'privacy_page_open',
    }),
    [],
  );

  return (
    <div className="pt-28 pb-16">
      <section className="py-10 hero-gradient">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f2540] mb-3">
            Privacy <span className="text-[#14A098]">Policy</span>
          </h1>
          <p className="text-gray-600">Last updated: June 2026</p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-14 space-y-6 text-gray-700 leading-relaxed">
        {SECTIONS.map((s) => (
          <div key={s.h}>
            <h2 className="text-xl font-bold text-[#0f2540] mb-2">{s.h}</h2>
            <div className="space-y-2">
              {s.blocks.map((block, idx) => <SectionBlock key={`${s.h}-${idx}`} block={block} />)}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default PrivacyPolicy;
